import { useEffect, useState, type FC, type ReactElement } from "react";
import { Button, Collapse, Flex, Radio, Table, Tabs } from "antd";
import {
  getCircleColor,
  getCircleColorBiologicals,
  getCircleColorNutrient,
} from "../../../../shared/layout/Plots/utils";
import "../../style.less";
import type { NewTrialType, ProtocolResponse, Replicants } from "../../types";
import TabPane from "antd/es/tabs/TabPane";
import ProtcolDrawer from "../../../../shared/layout/ProtocolDrawer";
import { InfoCircleOutlined, RightOutlined } from "@ant-design/icons";
import { useNewTrialState } from "../../hooks/NewTrialStateProvider";
import "./ratesAndDosage.less";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { useTranslation } from "react-i18next";
import { useTrialsRouter } from "../../../../shared/custom-hooks/useTrialsRouter";
import { getProductUnit } from "../../../create-protocol/utils";
import {
  calculatePlotAreaHa,
  calculateTotalAmount,
} from "../../../../shared/utils/areaCalculation";
import {
  calculateNutrientAmount,
  resolveNutrientComposition,
} from "../../../../shared/utils/nutrientCalculation";
import { createNutrientPlotColorScale } from "../../../../shared/utils/nutrientPlotColors";
import useNutrientCatalog from "../../../../shared/custom-hooks/useNutrientCatalog";
import {
  formatNumber,
  formatWithTwoDecimals,
} from "../../../../shared/utils/numberFormatting";
import { ReactComponent as SeedsIcon } from "../../../../assets/images/seeds.svg";
import { ReactComponent as SeedsGrayIcon } from "../../../../assets/images/seeds-gray.svg";
import { ReactComponent as BiologicalIcon } from "../../../../assets/images/bookmarks.svg";
import { ReactComponent as NutrientIcon } from "../../../../assets/images/soil.svg";
import { ReactComponent as NutrientGrayIcon } from "../../../../assets/images/soil-gray.svg";

const RepicantsLabel = ({
  data = [],
}: {
  data: Replicants[];
}): ReactElement => {
  const { t } = useTranslation();

  return (
    <>{`+ ${data.length} ${formatTranslation(
      t("newTrial.ratesAndDosage.replicants")
    )}`}</>
  );
};

interface SummaryRatesAndDosageProps {
  data: NewTrialType;
  trialProtocolData: ProtocolResponse | null;
  /**
   * Hides the "Protocol Info" tab action and its drawer. Set by the trial
   * preview, which already shows the linked protocol in its own section and
   * cannot nest a second drawer inside itself.
   */
  hideProtocolInfo?: boolean;
  /**
   * Renders the product-type glyph next to each tab label. Opt-in so the
   * Summary step keeps its current text-only tabs.
   */
  showTabIcons?: boolean;
  useBlueNutrientColors?: boolean;
}

/**
 * Tab glyph for a product type, using the same icons as the Protocol preview
 * drawer: blue while the tab is active, gray otherwise. `bookmarks` has no gray
 * variant in the asset set, so CP and Biological stays blue in both states —
 * matching the Protocol drawer.
 */
const TabIcon: FC<{ tabKey: string; isActive: boolean }> = ({
  tabKey,
  isActive,
}) => {
  switch (tabKey) {
    case "seeds":
      return isActive ? <SeedsIcon /> : <SeedsGrayIcon />;
    case "biologicals":
      return <BiologicalIcon />;
    case "fertilisers":
      return isActive ? <NutrientIcon /> : <NutrientGrayIcon />;
    default:
      return null;
  }
};

const RatesAndDosagesGrid: FC<{
  data: NewTrialType;
  trialProtocolData: ProtocolResponse | null;
  type: "seeds" | "fertilisers" | "biologicals";
  handleApplicationChange: (e: any) => void;
  applications?: any;
  bioApplicationIndex: number;
  useBlueNutrientColors: boolean;
}> = ({
  data,
  trialProtocolData,
  type,
  handleApplicationChange,
  applications,
  bioApplicationIndex,
  useBlueNutrientColors,
}) => {
  const { newTrialState, ratesDosage } = useNewTrialState();
  const { t } = useTranslation();
  const getNutrientColor = createNutrientPlotColorScale(
    data?.trial_plots?.flatMap((plot) =>
      (plot.properties?.fertilisers?.rates_and_dosages ?? []).map(
        (item) => item.rate
      )
    ) ?? []
  );

  const ratesAndDosagesObject =
    type === "biologicals" ? "treatments" : "rates_and_dosages";

  const selectedNutrient =
    trialProtocolData?.product_details?.nutrients?.products?.[0]?.nutrient_code;

  const nutrientCatalog = useNutrientCatalog(
    type === "fertilisers" ? trialProtocolData?.org_id : undefined,
    type === "fertilisers"
      ? trialProtocolData?.crop_details?.country
      : undefined
  );
  const standardFertilisers = (
    data?.standard_field_properties?.standard_fertilisers ?? []
  ).map((fertiliser) => {
    const catalogProduct = nutrientCatalog.data?.find((product) =>
      fertiliser.product_id
        ? product.id === fertiliser.product_id
        : product.name === fertiliser.fertiliser
    );
    return {
      ...fertiliser,
      nutrients: resolveNutrientComposition(
        catalogProduct,
        fertiliser.nutrients
      ),
    };
  });

  const showNutrientColumns =
    type === "fertilisers" &&
    !!selectedNutrient &&
    (data?.standard_field_properties?.standard_fertilisers?.length ?? 0) > 0;

  // A trial whose plots have not been laid out yet has nothing to tabulate, so
  // each tab says so instead of rendering an empty pane.
  if (!data?.trial_plots?.length) {
    return (
      <p className="rates-tab-empty" data-testid={`rates-empty-${type}`}>
        {formatTranslation(t("trialPreview.noData"))}
      </p>
    );
  }

  return (
    <Flex vertical={true} gap="10px">
      {type === "biologicals" && (
        <div className="section-a">
          <h5 className="summary-text-applications">
            {formatTranslation(t("newTrial.ratesAndDosage.applicationTiming"))}
          </h5>
          <Flex vertical gap="middle">
            <Radio.Group
              options={applications}
              value={bioApplicationIndex + 1}
              optionType="button"
              buttonStyle="solid"
              onChange={handleApplicationChange}
            />
          </Flex>
        </div>
      )}

      {data?.trial_plots?.map((plot, plotIndex: number) => {
        const isAnyStripPlot = data?.trial_plots?.some(
          (plot: any) => plot.plot?.isStripPlot
        );

        return (
          <div className="collapse-one" key={plotIndex}>
            <Collapse
              expandIconPosition="start"
              style={{ border: "none" }}
              defaultActiveKey={[plotIndex.toString()]}
            >
              <Collapse.Panel
                key={plotIndex.toString()}
                style={{
                  padding: "4px 8px",
                  backgroundColor: "#fff",
                  border: "none",
                }}
                header={
                  <Flex
                    className="plots-col"
                    align="center"
                    style={{ padding: "10px 0px" }}
                  >
                    <h4>
                      {/* One group holds every strip, so the label is plural. */}
                      {isAnyStripPlot
                        ? formatTranslation(t("plotDetails.strips"))
                        : formatTranslation(
                            t("trials.plotDesign.trialPlots.table.cell1")
                          )}{" "}
                      {isAnyStripPlot
                        ? ""
                        : String.fromCharCode(65 + plotIndex) + " "}
                    </h4>

                    {plot?.replicants?.length ? (
                      <RepicantsLabel data={plot.replicants || []} />
                    ) : null}
                  </Flex>
                }
              >
                {type === "seeds" && (
                  <Table
                    className="trialing-plot-table-container"
                    dataSource={plot?.properties?.[type]?.[
                      ratesAndDosagesObject
                    ]?.map((rateAndDosage: any, rateIndex: number) => {
                      const unit = plot?.properties?.seeds?.measure_unit || "";
                      // New assignments use a zero-based cell ID; legacy area
                      // lookup expects a one-based plot ID within this group.
                      const plotCellId: unknown = rateAndDosage?.plot_cell_id;
                      const plotId =
                        rateAndDosage?.plot_id ??
                        (typeof plotCellId === "number" &&
                        Number.isInteger(plotCellId) &&
                        plotCellId >= 0
                          ? plotCellId + 1
                          : undefined);
                      const savedQuantity = rateAndDosage?.quantity;
                      const quantity =
                        typeof savedQuantity === "number" &&
                        Number.isFinite(savedQuantity) &&
                        savedQuantity >= 0
                          ? savedQuantity
                          : calculateTotalAmount(
                              rateAndDosage?.rate || 0,
                              calculatePlotAreaHa(
                                plotId,
                                data.trial_plots,
                                newTrialState.stripsData,
                                plotIndex
                              ),
                              unit
                            );

                      return {
                        key: rateIndex,
                        index: rateIndex + 1,
                        product: rateAndDosage?.variety || "",
                        rate: rateAndDosage?.rate || 0,
                        unit,
                        quantity,
                        plot_id: plotId,
                        rateData: rateAndDosage,
                        color: getCircleColor(
                          rateAndDosage?.variety || "",
                          rateAndDosage?.rate || 0,
                          trialProtocolData?.product_details?.seeds?.products,
                          null,
                          plot?.properties?.seeds?.rates_and_dosages
                        ),
                      };
                    })}
                    columns={[
                      {
                        title: isAnyStripPlot
                          ? formatTranslation(t("plotDetails.strip"))
                          : formatTranslation(
                              t("trials.plotDesign.trialPlots.table.cell1")
                            ),
                        dataIndex: "index",
                        key: "index",
                        width: 60,
                        render: (value: number, record: any) => (
                          <div
                            className="plot-cell-color-bar-container"
                            style={{
                              background: record.color || "#DFE2E7",
                            }}
                          >
                            <div className="ml-15">{value}</div>
                          </div>
                        ),
                      },
                      {
                        title: formatTranslation(
                          t("trials.plotDesign.trialPlots.table.header2")
                        ),
                        children: [
                          {
                            title: formatTranslation(
                              t("createProtocol.objectives.objectiveItemName")
                            ),
                            dataIndex: "product",
                            key: "product",
                            render: (text: string) => (
                              <span
                                style={{
                                  padding: "12px 16px",
                                  display: "block",
                                  color: "#363948",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                }}
                              >
                                {text}
                              </span>
                            ),
                          },
                        ],
                      },
                      {
                        title: formatTranslation(
                          t("newTrial.ratesAndDosage.rateAndQty")
                        ),
                        children: [
                          {
                            title: plot?.properties?.seeds?.measure_unit || "",
                            dataIndex: "rate",
                            key: "rate",
                            width: 100,
                            render: (rate: number, record: any) => (
                              <span
                                style={{
                                  padding: "12px 16px",
                                  display: "block",
                                  color: "#363948",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                }}
                              >
                                {formatNumber(rate, record.unit, "rate")}
                              </span>
                            ),
                          },
                          {
                            title:
                              plot?.properties?.seeds?.measure_unit === "kg/ha"
                                ? formatTranslation(
                                    t(
                                      "trials.plotDesign.trialPlots.table.header.amountKg"
                                    )
                                  )
                                : formatTranslation(
                                    t("newTrial.ratesDosage.seeds")
                                  ),
                            dataIndex: "total",
                            key: "total",
                            width: 120,
                            render: (_: any, record: any) => {
                              return (
                                <span
                                  style={{
                                    padding: "12px 16px",
                                    display: "block",
                                    color: "#868CA2",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                  }}
                                >
                                  {formatNumber(
                                    record.quantity,
                                    record.unit,
                                    "total"
                                  )}
                                </span>
                              );
                            },
                          },
                        ],
                      },
                    ]}
                    pagination={false}
                    summary={(pageData) => {
                      const productTotals: Record<
                        string,
                        { amount: number; unit: string }
                      > = {};

                      const seedCalculatedTotalsByProduct = (
                        plot?.properties?.seeds as any
                      )?.calculated_totals?.by_product;

                      const hasNonZeroSeedCalculatedTotals =
                        Array.isArray(seedCalculatedTotalsByProduct) &&
                        seedCalculatedTotalsByProduct.some(
                          (item: any) =>
                            item?.variety && Number(item?.value) > 0
                        );

                      if (hasNonZeroSeedCalculatedTotals) {
                        seedCalculatedTotalsByProduct.forEach((item: any) => {
                          if (!item?.variety) return;

                          const itemUnit = String(
                            item?.measure_unit ||
                              plot?.properties?.seeds?.measure_unit ||
                              ""
                          ).toLowerCase();

                          productTotals[item.variety] = {
                            amount: Number(item?.value) || 0,
                            unit:
                              itemUnit.includes("kg") &&
                              !itemUnit.includes("seed")
                                ? formatTranslation(
                                    t(
                                      "trials.plotDesign.trialPlots.table.header.amountKg"
                                    )
                                  )
                                : formatTranslation(
                                    t("newTrial.ratesDosage.seeds")
                                  ),
                          };
                        });
                      } else {
                        pageData.forEach((record) => {
                          if (!productTotals[record.product]) {
                            productTotals[record.product] = {
                              amount: 0,
                              unit:
                                record.unit === "kg/ha"
                                  ? formatTranslation(
                                      t(
                                        "trials.plotDesign.trialPlots.table.header.amountKg"
                                      )
                                    )
                                  : formatTranslation(
                                      t("newTrial.ratesDosage.seeds")
                                    ),
                            };
                          }

                          productTotals[record.product].amount +=
                            record.quantity;
                        });
                      }

                      return (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={4}>
                            <div className="total-products-summary">
                              <span className="total-products-summary__label">
                                {formatTranslation(t("totalProducts"))}:
                              </span>

                              {Object.entries(productTotals).map(
                                ([product, productData]) => (
                                  <span
                                    key={product}
                                    className="total-products-summary__value"
                                  >
                                    {product}{" "}
                                    {productData.amount.toLocaleString(
                                      undefined,
                                      {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                      }
                                    )}{" "}
                                    {productData.unit}
                                  </span>
                                )
                              )}
                            </div>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      );
                    }}
                  />
                )}

                {type === "fertilisers" && (
                  <Table
                    className="trialing-plot-table-container"
                    dataSource={plot?.properties?.[type]?.[
                      ratesAndDosagesObject
                    ]?.map((rateAndDosage: any, rateIndex: number) => {
                      const fertilizers = standardFertilisers.map(
                        (fert: any) => ({
                          name: fert.fertiliser,
                          baseRate: fert.rate,
                          unit: fert.unit,
                          calculatedAmount:
                            ((rateAndDosage?.rate ?? 0) / 100) * fert.rate,
                        })
                      );

                      return {
                        key: rateIndex,
                        index: rateIndex + 1,
                        product:
                          fertilizers.map((f: any) => f.name).join(", ") ||
                          formatTranslation(
                            t("newTrial.ratesAndDosage.notApplicable")
                          ),
                        amount: rateAndDosage?.amount || 0,
                        rate: rateAndDosage?.rate || 0,
                        plot_id: rateAndDosage?.plot_id,
                        rateData: rateAndDosage,
                        fertilizers,
                        color: useBlueNutrientColors
                          ? getNutrientColor(rateAndDosage?.rate)
                          : ratesDosage?.rates_and_dosages?.[plotIndex]?.[
                              rateIndex
                            ]?.mapFillColor ??
                            rateAndDosage?.color ??
                            getCircleColorNutrient(
                              plot?.properties?.fertilisers?.rates_and_dosages,
                              rateAndDosage?.rate || 0,
                              "rate",
                              plotIndex
                            ),
                      };
                    })}
                    pagination={false}
                    columns={[
                      {
                        title: isAnyStripPlot
                          ? formatTranslation(t("plotDetails.strip"))
                          : formatTranslation(
                              t("trials.plotDesign.trialPlots.table.cell1")
                            ),
                        dataIndex: "index",
                        key: "index",
                        width: 60,
                        render: (value: number, record: any) => (
                          <div
                            className="plot-cell-color-bar-container"
                            style={{
                              background: record.color || "#DFE2E7",
                            }}
                          >
                            <span className="ml-15">{value}</span>
                          </div>
                        ),
                      },
                      {
                        title: formatTranslation(
                          t("trials.plotDesign.trialPlots.table.header.apply")
                        ),
                        children: [
                          {
                            title: "%",
                            dataIndex: "rate",
                            key: "rate",
                            width: 80,
                            render: (value: number) => (
                              <span
                                style={{
                                  padding: "12px 16px",
                                  display: "block",
                                }}
                              >
                                {formatNumber(value, "%", "percentage")}
                              </span>
                            ),
                          },
                        ],
                      },
                      {
                        title: formatTranslation(
                          t("trials.plotDesign.trialPlots.table.header2")
                        ),
                        children: [
                          {
                            title: formatTranslation(
                              t("newTrial.ratesAndDosage.kgPerHa")
                            ),
                            dataIndex: "amount",
                            key: "amount",
                            width: 100,
                            render: (value: number) => (
                              <span
                                style={{
                                  padding: "12px 16px",
                                  display: "block",
                                }}
                              >
                                {formatWithTwoDecimals(value)}
                              </span>
                            ),
                          },
                          {
                            title: formatTranslation(
                              t(
                                "trials.plotDesign.trialPlots.table.header.amountKg"
                              )
                            ),
                            key: "totalKg",
                            width: 100,
                            render: (_: any, record: any) => {
                              const areaHa = calculatePlotAreaHa(
                                record.plot_id,
                                data?.trial_plots || [],
                                newTrialState.stripsData,
                                plotIndex
                              );

                              const totalKg = calculateTotalAmount(
                                record.amount,
                                areaHa,
                                "kg/ha"
                              );

                              return (
                                <span
                                  style={{
                                    padding: "12px 16px",
                                    display: "block",
                                  }}
                                >
                                  {formatWithTwoDecimals(totalKg)}
                                </span>
                              );
                            },
                          },
                        ],
                      },
                      ...(showNutrientColumns
                        ? [
                            {
                              title: `${selectedNutrient} | ${
                                selectedNutrient === "N"
                                  ? formatTranslation(
                                      t(
                                        "trials.plotDesign.trialPlots.table.header.nutrient.N"
                                      )
                                    )
                                  : selectedNutrient === "P"
                                  ? formatTranslation(
                                      t(
                                        "trials.plotDesign.trialPlots.table.header.nutrient.P"
                                      )
                                    )
                                  : selectedNutrient === "K"
                                  ? formatTranslation(
                                      t(
                                        "trials.plotDesign.trialPlots.table.header.nutrient.K"
                                      )
                                    )
                                  : selectedNutrient
                              }`,
                              children: [
                                {
                                  title: formatTranslation(
                                    t("newTrial.ratesAndDosage.kgPerHa")
                                  ),
                                  key: "nutrientPerHa",
                                  width: 100,
                                  render: (_: any, record: any) => {
                                    const areaHa = calculatePlotAreaHa(
                                      record.plot_id,
                                      data?.trial_plots || [],
                                      newTrialState.stripsData,
                                      plotIndex
                                    );

                                    const nutrientAmounts =
                                      calculateNutrientAmount(
                                        standardFertilisers,
                                        record.amount,
                                        selectedNutrient ?? "",
                                        areaHa
                                      );

                                    return (
                                      <span
                                        style={{
                                          padding: "12px 16px",
                                          display: "block",
                                        }}
                                      >
                                        {formatWithTwoDecimals(
                                          nutrientAmounts.perHa
                                        )}
                                      </span>
                                    );
                                  },
                                },
                                {
                                  title: formatTranslation(
                                    t(
                                      "trials.plotDesign.trialPlots.table.header.amountKg"
                                    )
                                  ),
                                  key: "nutrientTotalKg",
                                  width: 100,
                                  render: (_: any, record: any) => {
                                    const areaHa = calculatePlotAreaHa(
                                      record.plot_id,
                                      data?.trial_plots || [],
                                      newTrialState.stripsData,
                                      plotIndex
                                    );

                                    const nutrientAmounts =
                                      calculateNutrientAmount(
                                        standardFertilisers,
                                        record.amount,
                                        selectedNutrient ?? "",
                                        areaHa
                                      );

                                    return (
                                      <span
                                        style={{
                                          padding: "12px 16px",
                                          display: "block",
                                        }}
                                      >
                                        {formatWithTwoDecimals(
                                          nutrientAmounts.total
                                        )}
                                      </span>
                                    );
                                  },
                                },
                              ],
                            },
                          ]
                        : []),
                    ]}
                    summary={(pageData) => {
                      const productTotals: Record<string, number> = {};

                      const fertiliserCalculatedTotalsByProduct = (
                        plot?.properties?.fertilisers as any
                      )?.calculated_totals?.by_product;

                      if (
                        Array.isArray(fertiliserCalculatedTotalsByProduct) &&
                        fertiliserCalculatedTotalsByProduct.length > 0
                      ) {
                        fertiliserCalculatedTotalsByProduct.forEach(
                          (item: any) => {
                            if (!item?.variety) return;
                            productTotals[item.variety] =
                              Number(item?.value) || 0;
                          }
                        );
                      } else {
                        pageData.forEach((record) => {
                          const areaHa = calculatePlotAreaHa(
                            record.plot_id,
                            data?.trial_plots || [],
                            newTrialState.stripsData,
                            plotIndex
                          );

                          record.fertilizers.forEach((fert: any) => {
                            const totalKg = calculateTotalAmount(
                              fert.calculatedAmount,
                              areaHa,
                              "kg/ha"
                            );

                            if (!productTotals[fert.name]) {
                              productTotals[fert.name] = 0;
                            }

                            productTotals[fert.name] += totalKg;
                          });
                        });
                      }

                      // Saved trials can retain amounts without product names.
                      const unassignedProductTotal =
                        Object.keys(productTotals).length === 0
                          ? pageData.reduce(
                              (total, record) =>
                                total +
                                calculateTotalAmount(
                                  record.amount,
                                  calculatePlotAreaHa(
                                    record.plot_id,
                                    data?.trial_plots || [],
                                    newTrialState.stripsData,
                                    plotIndex
                                  ),
                                  "kg/ha"
                                ),
                              0
                            )
                          : null;

                      const colSpan = showNutrientColumns ? 6 : 4;

                      return (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={colSpan}>
                            <div className="total-products-summary">
                              <span className="total-products-summary__label">
                                {formatTranslation(t("totalProducts"))}:
                              </span>

                              {unassignedProductTotal !== null && (
                                <span className="total-products-summary__value">
                                  {formatWithTwoDecimals(
                                    unassignedProductTotal
                                  )}{" "}
                                  {formatTranslation(
                                    t(
                                      "trials.plotDesign.trialPlots.table.header.amountKg"
                                    )
                                  )}
                                </span>
                              )}

                              {Object.entries(productTotals).map(
                                ([product, amount]) => (
                                  <span
                                    key={product}
                                    className="total-products-summary__value"
                                  >
                                    {product} {formatWithTwoDecimals(amount)}{" "}
                                    {formatTranslation(
                                      t(
                                        "trials.plotDesign.trialPlots.table.header.amountKg"
                                      )
                                    )}
                                  </span>
                                )
                              )}
                            </div>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      );
                    }}
                  />
                )}

                {type === "biologicals" && (
                  <Table
                    className="trialing-plot-table-container"
                    dataSource={plot?.properties?.[type]?.[
                      ratesAndDosagesObject
                    ]?.[bioApplicationIndex]?.map(
                      (application: any, plotIdx: number) => {
                        const treatmentId =
                          application?.selected_treatment?.[0];

                        const treatmentData = treatmentId
                          ? trialProtocolData?.product_details?.biologicals
                              ?.treatments?.[treatmentId - 1]
                          : null;

                        const isStripPlot = plot?.plot?.isStripPlot;
                        let constructedPlotId: string;

                        if (isStripPlot) {
                          constructedPlotId = String(plotIdx + 1);
                        } else {
                          const plotGroupId = plot?.plot?.id ?? plotIndex;
                          const columns = plot?.plot?.columns ?? 1;
                          const rowIdx = Math.floor(plotIdx / columns);
                          const colIdx = plotIdx % columns;

                          constructedPlotId = `${String(plotGroupId)}-${String(
                            rowIdx
                          )}-${String(colIdx)}`;
                        }

                        return {
                          key: plotIdx,
                          index: plotIdx + 1,
                          treated: application?.treated,
                          treatmentId: treatmentId ?? null,
                          treatmentNumber: application?.treated
                            ? String(treatmentId)
                            : "-",
                          treatment:
                            application?.treated && treatmentData
                              ? `${String(treatmentId)} - ${formatTranslation(
                                  t("plotDetails.treated")
                                )}`
                              : formatTranslation(t("plotDetails.untreated")),
                          treatmentName: treatmentData?.instructions ?? "",
                          products: treatmentData?.products ?? [],
                          plot_id: constructedPlotId,
                          color: getCircleColorBiologicals(
                            application,
                            application?.selected_treatment?.[0]
                              ? application?.selected_treatment[0]
                              : null
                          ),
                          tankMixRate: treatmentData?.tank_mix_rate ?? 0,
                        };
                      }
                    )}
                    columns={[
                      {
                        title: isAnyStripPlot
                          ? formatTranslation(t("plotDetails.strip"))
                          : formatTranslation(
                              t("trials.plotDesign.trialPlots.table.cell1")
                            ),
                        dataIndex: "index",
                        key: "index",
                        width: 80,
                        render: (value: number, record: any) => (
                          <div
                            className="plot-cell-color-bar-container"
                            style={{
                              background: record.color || "#DFE2E7",
                            }}
                          >
                            <span className="ml-15">{value}</span>
                          </div>
                        ),
                      },
                      {
                        title: formatTranslation(
                          t(
                            "createProtocol.products.biologicals.product.treatmentTitle"
                          )
                        ),
                        dataIndex: "treatment",
                        key: "treatment",
                        render: (_text: string, record: any) => (
                          <span
                            style={{
                              padding: "12px 16px",
                              display: "block",
                              color: "#363948",
                              fontSize: "14px",
                              fontWeight: 400,
                            }}
                          >
                            {record.treated
                              ? `${String(
                                  record.treatmentId
                                )} - ${formatTranslation(
                                  t("plotDetails.treated")
                                )}`
                              : formatTranslation(t("plotDetails.untreated"))}
                          </span>
                        ),
                      },
                      {
                        title: formatTranslation(
                          t("newTrial.ratesAndDosage.rateAndQty")
                        ),
                        children: [
                          {
                            title: formatTranslation(
                              t(
                                "createProtocol.products.biologicals.product.ratePlaceholder"
                              )
                            ),
                            key: "rate",
                            width: 120,
                            render: () => (
                              <span
                                style={{
                                  padding: "12px 16px",
                                  display: "block",
                                  color: "#868CA2",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                }}
                              >
                                -
                              </span>
                            ),
                          },
                          {
                            title: formatTranslation(
                              t("newTrial.ratesAndDosage.vol")
                            ),
                            key: "volume",
                            width: 100,
                            render: () => (
                              <span
                                style={{
                                  padding: "12px 16px",
                                  display: "block",
                                  color: "#868CA2",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                }}
                              >
                                -
                              </span>
                            ),
                          },
                        ],
                      },
                    ]}
                    expandable={{
                      expandIcon: ({ expanded, onExpand, record }) => {
                        if (record.treated && record.products.length > 0) {
                          return (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                onExpand(record, e);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  onExpand(record, e as any);
                                }
                              }}
                              aria-label={
                                expanded
                                  ? formatTranslation(
                                      t(
                                        "newTrial.ratesAndDosage.collapseTreatmentDetails"
                                      )
                                    )
                                  : formatTranslation(
                                      t(
                                        "newTrial.ratesAndDosage.expandTreatmentDetails"
                                      )
                                    )
                              }
                              style={{
                                cursor: "pointer",
                                border: "none",
                                background: "none",
                                padding: 0,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <RightOutlined
                                style={{
                                  fontSize: "12px",
                                  color: "#8C8C8C",
                                  transition: "transform 0.2s",
                                  transform: expanded
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                                }}
                              />
                            </button>
                          );
                        }

                        return null;
                      },
                      expandIconColumnIndex: 3,
                      expandedRowRender: (record: any) => {
                        if (!record.treated || record.products.length === 0) {
                          return null;
                        }

                        const areaHa = calculatePlotAreaHa(
                          record.plot_id,
                          data?.trial_plots || [],
                          newTrialState.stripsData,
                          plotIndex
                        );

                        return (
                          <Table
                            showHeader={false}
                            dataSource={record.products.map(
                              (product: any, idx: number) => {
                                const productVolume = calculateTotalAmount(
                                  product.rate,
                                  areaHa,
                                  product.measure_unit
                                );

                                const displayUnit = getProductUnit(
                                  product.measure_unit
                                );

                                return {
                                  key: idx,
                                  name: product.variety,
                                  rate: product.rate,
                                  rateUnit: displayUnit,
                                  volume: productVolume,
                                  volumeUnit: displayUnit.replace("/ha", ""),
                                };
                              }
                            )}
                            columns={[
                              {
                                dataIndex: "empty1",
                                key: "empty1",
                                width: 80,
                                render: () => (
                                  <span
                                    style={{
                                      padding: "12px 16px",
                                      display: "block",
                                    }}
                                  ></span>
                                ),
                              },
                              {
                                dataIndex: "name",
                                key: "name",
                                render: (text: string) => (
                                  <span
                                    style={{
                                      padding: "12px 16px",
                                      display: "block",
                                      color: "#262626",
                                      fontSize: "14px",
                                      fontWeight: 400,
                                    }}
                                  >
                                    {text}
                                  </span>
                                ),
                              },
                              {
                                dataIndex: "rate",
                                key: "rate",
                                width: 120,
                                render: (rate: number, record: any) => (
                                  <span
                                    style={{
                                      padding: "12px 16px",
                                      display: "block",
                                      color: "#363948",
                                      fontSize: "14px",
                                      fontWeight: 400,
                                    }}
                                  >
                                    {formatWithTwoDecimals(rate)}
                                    {record.rateUnit}
                                  </span>
                                ),
                              },
                              {
                                dataIndex: "volume",
                                key: "volume",
                                width: 100,
                                render: (volume: number, record: any) => (
                                  <span
                                    style={{
                                      padding: "12px 16px",
                                      display: "block",
                                      color: "#868CA2",
                                      fontSize: "14px",
                                      fontWeight: 400,
                                    }}
                                  >
                                    {formatWithTwoDecimals(volume)}
                                    {record.volumeUnit}
                                  </span>
                                ),
                              },
                              {
                                dataIndex: "empty2",
                                key: "empty2",
                                width: 48,
                                render: () => (
                                  <span
                                    style={{
                                      padding: "12px 16px",
                                      display: "block",
                                    }}
                                  ></span>
                                ),
                              },
                            ]}
                            pagination={false}
                            bordered={false}
                            className="nested-product-table"
                          />
                        );
                      },
                      rowExpandable: (record: any) =>
                        record.treated && record.products.length > 0,
                      indentSize: 0,
                    }}
                    pagination={false}
                    summary={(pageData) => {
                      const productTotals: Record<
                        string,
                        { totalVolume: number; unit: string }
                      > = {};

                      const byApplication = (
                        plot?.properties?.biologicals as any
                      )?.calculated_totals?.by_application;

                      const selectedApplicationTotals = Array.isArray(
                        byApplication?.[bioApplicationIndex]
                      )
                        ? byApplication[bioApplicationIndex]
                        : [];

                      const hasNonZeroBiologicalCalculatedTotals =
                        selectedApplicationTotals.some(
                          (item: any) =>
                            item?.variety && Number(item?.value) > 0
                        );

                      if (hasNonZeroBiologicalCalculatedTotals) {
                        selectedApplicationTotals.forEach((item: any) => {
                          if (!item?.variety) return;

                          productTotals[item.variety] = {
                            totalVolume: Number(item?.value) || 0,
                            unit: getProductUnit(item?.measure_unit || ""),
                          };
                        });
                      } else {
                        const treatmentTotals: Record<
                          string,
                          {
                            products: Record<
                              string,
                              {
                                rate: number;
                                unit: string;
                                totalVolume: number;
                              }
                            >;
                          }
                        > = {};

                        pageData.forEach((record) => {
                          if (record.treated && record.treatmentId) {
                            const treatmentKey = `Treatment ${String(
                              record.treatmentId
                            )}`;

                            if (!treatmentTotals[treatmentKey]) {
                              treatmentTotals[treatmentKey] = { products: {} };
                            }

                            const areaHa = calculatePlotAreaHa(
                              record.plot_id,
                              data?.trial_plots || [],
                              newTrialState.stripsData,
                              plotIndex
                            );

                            record.products.forEach((product: any) => {
                              const productVolume = calculateTotalAmount(
                                product.rate,
                                areaHa,
                                product.measure_unit
                              );

                              if (
                                !treatmentTotals[treatmentKey].products[
                                  product.variety
                                ]
                              ) {
                                treatmentTotals[treatmentKey].products[
                                  product.variety
                                ] = {
                                  rate: product.rate,
                                  unit: getProductUnit(product.measure_unit),
                                  totalVolume: 0,
                                };
                              }

                              treatmentTotals[treatmentKey].products[
                                product.variety
                              ].totalVolume += productVolume;
                            });
                          }
                        });

                        Object.values(treatmentTotals).forEach(
                          (treatmentData) => {
                            Object.entries(treatmentData.products).forEach(
                              ([productName, productData]) => {
                                if (!productTotals[productName]) {
                                  productTotals[productName] = {
                                    totalVolume: 0,
                                    unit: productData.unit,
                                  };
                                }

                                productTotals[productName].totalVolume +=
                                  productData.totalVolume;
                              }
                            );
                          }
                        );
                      }

                      if (Object.keys(productTotals).length === 0) {
                        return null;
                      }

                      return (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={5}>
                            <div className="total-products-summary">
                              <span className="total-products-summary__label">
                                {formatTranslation(t("totalProducts"))}:
                              </span>

                              {Object.entries(productTotals).map(
                                ([productName, productData]) => (
                                  <span
                                    key={productName}
                                    className="total-products-summary__value"
                                  >
                                    {productName}{" "}
                                    {formatWithTwoDecimals(
                                      productData.totalVolume
                                    )}{" "}
                                    {productData.unit.replace("/ha", "")}
                                  </span>
                                )
                              )}
                            </div>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      );
                    }}
                  />
                )}
              </Collapse.Panel>
            </Collapse>
          </div>
        );
      })}
    </Flex>
  );
};

const SummaryRatesAndDosage: FC<SummaryRatesAndDosageProps> = ({
  data,
  trialProtocolData,
  hideProtocolInfo = false,
  showTabIcons = false,
  useBlueNutrientColors = false,
}) => {
  const {
    setIsFertilizerTabActive,
    setActiveRatesTab,
    setBioApplicationIndex,
    bioApplicationIndex,
  } = useNewTrialState();
  const { t } = useTranslation();

  const tabs: Array<{ key: string; label: string }> = [];
  const { orgId, workspaceId } = useTrialsRouter();
  const extraHeaders = {
    "org-id": orgId ?? "",
    "workspace-id": workspaceId ?? "",
  };

  const [showDrawer, setShowDrawer] = useState(false);

  // Tabs stay uncontrolled; this only tracks which glyph renders in its active
  // (blue) colour, so it has to start on whichever tab antd opens first.
  const [activeTabKey, setActiveTabKey] = useState("");

  const hideDrawer = (): void => {
    setShowDrawer(false);
  };

  const openDrawer = (): void => {
    setShowDrawer(true);
  };

  if (trialProtocolData?.protocol_type?.includes("seed")) {
    tabs.push({
      key: "seeds",
      label: formatTranslation(t("newTrial.ratesDosage.seeds")),
    });
  }

  if (trialProtocolData?.protocol_type?.includes("biological")) {
    tabs.push({
      key: "biologicals",
      label: formatTranslation(t("newTrial.ratesAndDosage.cpAndBiological")),
    });
  }

  if (trialProtocolData?.protocol_type?.includes("nutrient")) {
    tabs.push({
      key: "fertilisers",
      label: formatTranslation(t("newTrial.ratesAndDosage.fertilizers")),
    });
  }

  const firstTabKey = tabs[0]?.key;
  useEffect(() => {
    setIsFertilizerTabActive(firstTabKey === "fertilisers");
    setActiveRatesTab(
      firstTabKey === "seeds"
        ? "seed"
        : firstTabKey === "fertilisers"
        ? "nutrient"
        : firstTabKey === "biologicals"
        ? "biological"
        : ""
    );
    setActiveTabKey(firstTabKey ?? "");

    return () => {
      setIsFertilizerTabActive(false);
    };
  }, [firstTabKey, setActiveRatesTab, setIsFertilizerTabActive]);

  useEffect(() => {
    setBioApplicationIndex(0);
  }, []);

  const handleApplicationChange = (e: any): void => {
    setBioApplicationIndex(e.target.value - 1);
  };

  return (
    <div>
      {!hideProtocolInfo && (
        <ProtcolDrawer
          open={showDrawer}
          onClose={hideDrawer}
          protocolId={trialProtocolData?.id ?? ""}
          extraHeaders={extraHeaders}
        />
      )}

      <Tabs
        // Must match a rendered pane, or antd's default selection and the
        // active-tab key driving the glyphs fall out of sync.
        defaultActiveKey={tabs[0]?.key}
        tabBarExtraContent={
          hideProtocolInfo ? null : (
            <Button
              type="text"
              onClick={openDrawer}
              icon={<InfoCircleOutlined />}
            >
              {formatTranslation(t("newTrial.ratesAndDosage.protocolInfo"))}
            </Button>
          )
        }
        onChange={(key) => {
          setIsFertilizerTabActive(key === "fertilisers");
          setActiveTabKey(key);

          key === "seeds"
            ? setActiveRatesTab("seed")
            : key === "fertilisers"
            ? setActiveRatesTab("nutrient")
            : setActiveRatesTab("biological");
        }}
      >
        {tabs.map((tab) => {
          const applicationCount =
            trialProtocolData?.product_details?.biologicals?.applications
              ?.length ?? 0;

          const applications =
            tab.key === "biologicals"
              ? Array.from({ length: applicationCount }, (_, index) => ({
                  label: `${index + 1}`,
                  value: index + 1,
                }))
              : [];

          return (
            <TabPane
              tab={
                showTabIcons ? (
                  <span
                    className="rates-tab-label"
                    data-testid={`rates-tab-${tab.key}`}
                  >
                    <TabIcon
                      tabKey={tab.key}
                      isActive={activeTabKey === tab.key}
                    />
                    {tab.label}
                  </span>
                ) : (
                  tab.label
                )
              }
              key={tab.key}
            >
              <RatesAndDosagesGrid
                data={data}
                trialProtocolData={trialProtocolData}
                type={tab.key as "seeds" | "fertilisers" | "biologicals"}
                handleApplicationChange={handleApplicationChange}
                applications={applications}
                bioApplicationIndex={bioApplicationIndex}
                useBlueNutrientColors={useBlueNutrientColors}
              />
            </TabPane>
          );
        })}
      </Tabs>
    </div>
  );
};

export default SummaryRatesAndDosage;
